import { useCallback } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { DeleteUserErrorReason } from "@dealer-center/ddc-pages-configuration-interface";
import {
  CONFIGURATION_QUERY_KEY_BASE,
  type ConfigurationWriteRequestError,
  configurationWriteRequest
} from "./configurationRequest";
import { removeUserFromListCache } from "./userCacheWrites";

export interface DeleteUserVariables {
  /** Required by the BFF; CC9's delete ignores it. */
  accountId: string;
  userId: string;
}

const PER_USER_RESOURCES = [
  "userinfo",
  "accountaccess",
  "featureaccess",
  "loginhistory"
] as const;

/**
 * Cache cleanup is not done in `onSuccess`: that runs before the caller's, while the deleted user is
 * still selected, so `useGetUserInfo` would refetch and flash a 404. The page calls
 * `removeDeletedUserQueries` after clearing the selection. The list row is removed, not invalidated,
 * because CC9 reads lag writes.
 */
export const useDeleteUserMutation = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation<
    void,
    ConfigurationWriteRequestError<DeleteUserErrorReason>,
    DeleteUserVariables
  >({
    mutationFn: ({ accountId, userId }) =>
      configurationWriteRequest<DeleteUserErrorReason>(
        { resource: "user", userId, accountId },
        undefined,
        "DELETE"
      )
  });

  const removeDeletedUserQueries = useCallback(
    ({ accountId, userId }: DeleteUserVariables) => {
      PER_USER_RESOURCES.forEach(resource => {
        queryClient.removeQueries({
          queryKey: [CONFIGURATION_QUERY_KEY_BASE, resource, accountId, userId]
        });
      });
      queryClient.removeQueries({
        queryKey: [CONFIGURATION_QUERY_KEY_BASE, "bridgeid", userId]
      });
      return removeUserFromListCache(queryClient, accountId, userId);
    },
    [queryClient]
  );

  return { ...mutation, removeDeletedUserQueries };
};
